import React, {
  memo,
  useCallback,
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { Pressable, type LayoutChangeEvent, type TextLayoutEvent } from 'react-native';

import { Stack } from '@/components/layout';
import { Collapsible, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';
import { BODY_HEIGHT_TOLERANCE, COLLAPSED_BODY_LINES } from './constants';
import type { CoachNote } from './types';
import { formatCoachNoteDate } from './utils';

const strings = STRINGS.guardian.home.newNotes;

function useBodyHeightHandler(setHeight: Dispatch<SetStateAction<number>>) {
  return useCallback(
    (event: LayoutChangeEvent) => {
      const nextHeight = Math.ceil(event.nativeEvent.layout.height);
      setHeight(prev => (prev === nextHeight ? prev : nextHeight));
    },
    [setHeight],
  );
}

type NewNoteCardProps = {
  note: CoachNote;
  onPress?: (note: CoachNote) => void;
};

function NewNoteCard({ note, onPress }: NewNoteCardProps) {
  const styles = useStyles();
  const { scale } = useScaling();
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const [collapsedBodyHeight, setCollapsedBodyHeight] = useState(0);
  const [measuredBodyHeight, setMeasuredBodyHeight] = useState(0);

  const handlePress = useCallback(() => {
    onPress?.(note);
  }, [note, onPress]);

  const handleBodyTextLayout = useCallback((event: TextLayoutEvent) => {
    const shouldExpand = event.nativeEvent.lines.length > COLLAPSED_BODY_LINES;

    setCanExpand(prev => (prev === shouldExpand ? prev : shouldExpand));
    if (!shouldExpand) setExpanded(false);
  }, []);

  const handleCollapsedBodyLayout = useBodyHeightHandler(setCollapsedBodyHeight);
  const handleMeasuredBodyLayout = useBodyHeightHandler(setMeasuredBodyHeight);

  useEffect(() => {
    setExpanded(false);
    setCanExpand(false);
    setCollapsedBodyHeight(0);
    setMeasuredBodyHeight(0);
  }, [note.body]);

  useEffect(() => {
    if (!collapsedBodyHeight || !measuredBodyHeight) return;

    const shouldExpand = measuredBodyHeight > collapsedBodyHeight + BODY_HEIGHT_TOLERANCE;
    setCanExpand(prev => (prev === shouldExpand ? prev : shouldExpand));
    if (!shouldExpand) setExpanded(false);
  }, [collapsedBodyHeight, measuredBodyHeight]);

  return (
    <Stack style={styles.root}>
      <Stack direction="row" align="center" justify="space-between" gap={scale(8)}>
        <Pressable
          accessibilityRole={onPress ? 'button' : undefined}
          disabled={!onPress}
          hitSlop={scale(6)}
          onPress={handlePress}
          style={({ pressed }) => [styles.titleButton, pressed && styles.pressed]}>
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 12 Bold"
            numberOfLines={1}
            style={styles.title}>
            {note.playerName}
          </Text>
        </Pressable>
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 11 Regular"
          numberOfLines={1}
          style={styles.date}>
          {formatCoachNoteDate(note.date)}
        </Text>
      </Stack>

      {!expanded ? (
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
          numberOfLines={COLLAPSED_BODY_LINES}
          onLayout={handleCollapsedBodyLayout}
          style={styles.body}>
          {note.body}
        </Text>
      ) : null}

      <Text
        accessibilityElementsHidden
        onLayout={handleMeasuredBodyLayout}
        onTextLayout={handleBodyTextLayout}
        pointerEvents="none"
        variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
        style={[styles.body, styles.measureBody]}>
        {note.body}
      </Text>

      {canExpand ? (
        <Collapsible open={expanded} onOpenChange={setExpanded} animated={true}>
          <Collapsible.Content>
            <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.body}>
              {note.body}
            </Text>
          </Collapsible.Content>
          <Collapsible.Trigger hideDefaultIcon triggerStyle={styles.toggleTrigger}>
            {isOpen => (
              <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.toggle}>
                {isOpen ? strings.showLess : strings.viewMore}
              </Text>
            )}
          </Collapsible.Trigger>
        </Collapsible>
      ) : null}

      <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.coach}>
        {strings.coachLabel}:
        <Text variant="Font Guardian App/Subtitle/Subtitle 12 Bold" style={styles.coachName}>
          {` ${note.coachName}`}
        </Text>
      </Text>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    paddingHorizontal: scale(16),
    paddingVertical: scale(12),
    borderWidth: 0.5,
    borderColor: guardianColors['12'],
    borderRadius: scale(12),
    backgroundColor: guardianColors['11'],
  },
  pressed: {
    opacity: 0.82,
  },
  titleButton: {
    flexShrink: 1,
  },
  title: {
    color: guardianColors['10'],
  },
  date: {
    flexShrink: 0,
    color: guardianColors['6'],
  },
  body: {
    marginTop: scale(8),
    color: guardianColors['10'],
  },
  measureBody: {
    position: 'absolute',
    left: scale(16),
    right: scale(16),
    top: scale(28),
    opacity: 0,
  },
  toggleTrigger: {
    alignSelf: 'flex-start',
  },
  toggle: {
    color: guardianColors.Blue,
    textDecorationLine: 'underline',
  },
  coach: {
    marginTop: scale(8),
    color: guardianColors['10'],
  },
  coachName: {
    color: guardianColors['10'],
  },
}));

export default memo(NewNoteCard);
